import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { UserPlus, Pencil, Trash2, ShieldAlert, Users } from "lucide-react";
import { toast } from "sonner";

const ROLE_OPTIONS = {
  VENDOR: ["VENDOR_ADMIN", "SALES_MANAGER", "SALES_EXECUTIVE", "PRODUCTION_MANAGER", "QC_MANAGER", "DISPATCH_MANAGER", "WAREHOUSE_MANAGER", "FINANCE_MANAGER", "ACCOUNTS_EXECUTIVE"],
  DISTRIBUTOR: ["SALES_MANAGER", "SALES_EXECUTIVE", "SALES_COORDINATOR", "WAREHOUSE_MANAGER", "DISPATCH_MANAGER", "FINANCE_MANAGER", "ACCOUNTS_EXECUTIVE", "CA"],
};

/**
 * Portal Team management — used by Vendor Admin and Distributor.
 * - Add / edit / delete members
 * - Plan seat usage bar (blocks add when plan max reached)
 * - Role dropdown scoped by portal
 */
export default function PortalTeam({ portal = "VENDOR", title = "Team & staff" }) {
  const { user } = useAuth();
  const [team, setTeam] = useState({ members: [], plan: {} });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", full_name: "", role: ROLE_OPTIONS[portal][0], phone: "", price_access: false });

  const load = async () => {
    try { const { data } = await api.get("/portal/team"); setTeam(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, []);

  const maxUsers = team.plan?.max_users;
  const used = team.members.length;
  const pct = maxUsers ? Math.min(100, Math.round((used / maxUsers) * 100)) : 0;

  const openAdd = () => {
    setEditing(null);
    setForm({ email: "", full_name: "", role: ROLE_OPTIONS[portal][0], phone: "", price_access: false });
    setOpen(true);
  };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ email: m.email, full_name: m.full_name || "", role: m.role, phone: m.phone || "", price_access: !!m.price_access });
    setOpen(true);
  };
  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/portal/team/${editing.id}`, { full_name: form.full_name, role: form.role, phone: form.phone, price_access: form.price_access });
        toast.success("Member updated");
      } else {
        await api.post("/portal/team", form);
        toast.success("Member added · temp password 'timesphere@2026'");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.full_name || m.email}?`)) return;
    try { await api.delete(`/portal/team/${m.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const seatFull = maxUsers && used >= maxUsers;
  const roleList = ROLE_OPTIONS[portal] || ROLE_OPTIONS.VENDOR;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="portal-team">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">{title}</h1>
          <p className="text-muted-foreground">Add and manage your portal team. Seats and roles are scoped by your active subscription plan.</p>
        </div>
        <Button onClick={openAdd} disabled={seatFull} data-testid="team-add-btn">
          <UserPlus className="w-4 h-4 mr-1" /> Add member
        </Button>
      </div>

      {/* Seat usage */}
      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <div className="flex items-center gap-2"><Users className="w-4 h-4 text-muted-foreground" /><span className="font-semibold">Plan seats</span> · <Badge variant="outline" className="font-mono text-[10px]">{team.plan?.code || "—"}</Badge></div>
          <span className="font-mono text-xs text-muted-foreground">{used} / {maxUsers ?? "—"} used</span>
        </div>
        <Progress value={pct} className={pct >= 90 ? "[&>div]:bg-red-500" : pct >= 70 ? "[&>div]:bg-amber-500" : ""} />
        {seatFull && (
          <div className="mt-2 text-xs text-red-700 flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Seat limit reached. Upgrade your plan to add more members.</div>
        )}
      </div>

      {/* Members table */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Member</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead>
            <TableHead>Price access</TableHead><TableHead>Phone</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {team.members.map((m) => (
              <TableRow key={m.id} data-testid={`team-row-${m.id}`}>
                <TableCell><div className="font-medium">{m.full_name || "—"}</div><div className="text-xs text-muted-foreground">{m.email}</div></TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-[10px]">{m.role}</Badge></TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-[10px]">{m.account_status}</Badge></TableCell>
                <TableCell>{m.price_access ? <Badge>ON</Badge> : <Badge variant="outline">OFF</Badge>}</TableCell>
                <TableCell className="text-xs">{m.phone || "—"}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(m)} data-testid={`team-edit-${m.id}`}><Pencil className="w-4 h-4" /></Button>
                  {m.id !== user?.id && <Button size="sm" variant="ghost" onClick={() => remove(m)} data-testid={`team-delete-${m.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {team.members.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No members yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="team-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit member" : "Add team member"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Full name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="team-name" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} data-testid="team-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="team-role"><SelectValue /></SelectTrigger>
                <SelectContent>{roleList.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.price_access} onChange={(e) => setForm({ ...form, price_access: e.target.checked })} />
              Grant price visibility (needed for finance / management roles)
            </label>
            {!editing && <p className="text-xs text-muted-foreground">Temp password <code className="font-mono">timesphere@2026</code> will be set. The member should change it after first sign-in.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="team-save">{editing ? "Update" : "Add"} member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
